// src/pages/dashboard/AccountSettings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, Smartphone, PauseCircle, Download, Trash2, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import Toggle from '../../components/Toggle';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import firestoreService from '../../services/firestore-service';
import { auth, db } from '../../config/firebase-config';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import LoadingButton from '../../components/common/LoadingButton';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const DELETE_REASONS = [
  'Found a different platform',
  'No longer offering services',
  'Too expensive',
  'Not enough clients or bookings',
  'Technical issues',
  'Privacy concerns',
  'Other',
];

const DELETE_REASONS_SEEKER = [
  'No longer looking for sessions',
  'Found what I needed',
  'Too expensive',
  'Technical issues',
  'Privacy concerns',
  'Other',
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

const triggerDownload = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────────────────────
// ExportSection — standalone "Download my data" card
// ─────────────────────────────────────────────────────────────────────────────

const ExportSection = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportResult, setExportResult] = useState(null);

  const handleExport = async () => {
    setIsExporting(true);
    setExportError('');
    setExportDone(false);
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const exportUserData = httpsCallable(functions, 'exportUserData');
      const result = await exportUserData({});
      setExportResult(result.data);
      setExportDone(true);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadJSON = () => {
    if (!exportResult?.data) return;
    const filename = `df-data-export-${new Date().toISOString().slice(0, 10)}.json`;
    triggerDownload(JSON.stringify(exportResult.data, null, 2), filename, 'application/json');
  };

  const handleDownloadCSV = () => {
    if (!exportResult?.bookingsCsv) return;
    const filename = `df-bookings-export-${new Date().toISOString().slice(0, 10)}.csv`;
    triggerDownload(exportResult.bookingsCsv, filename, 'text/csv');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">Your data</h3>
          <p className="text-sm text-gray-600 mb-4">
            Download a copy of all your Divine Frameworks data including your profile, bookings, clients, and session history.
          </p>

          {!exportDone ? (
            <>
              {exportError && (
                <p className="text-sm text-red-600 mb-3">{exportError} Contact support if this persists.</p>
              )}
              <LoadingButton
                onClick={handleExport}
                loading={isExporting}
                loadingText="Preparing your data..."
                className="px-4 py-2 border border-purple-300 text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors"
              >
                Download my data
              </LoadingButton>
            </>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-700">Your data is ready!</span>
              </div>
              <div className="flex gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleDownloadJSON}
                  className="px-4 py-2 border border-purple-300 text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors text-sm"
                >
                  Download JSON
                </button>
                {exportResult?.bookingsCsv && (
                  <button
                    type="button"
                    onClick={handleDownloadCSV}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
                  >
                    Download Bookings CSV
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Download links are available for this session.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DeleteModal — 3-step deletion flow
// ─────────────────────────────────────────────────────────────────────────────

const DeleteModal = ({ role, onClose, onDeleted }) => {
  const [step, setStep] = useState(1);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteFeedback, setDeleteFeedback] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Export sub-state (used in Step 1)
  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  const isPractitioner = role !== 'seeker';
  const reasons = isPractitioner ? DELETE_REASONS : DELETE_REASONS_SEEKER;

  const handleExportInModal = async () => {
    setIsExporting(true);
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const exportUserData = httpsCallable(functions, 'exportUserData');
      const result = await exportUserData({});
      setExportResult(result.data);
      setExportDone(true);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadJSON = () => {
    if (!exportResult?.data) return;
    triggerDownload(
      JSON.stringify(exportResult.data, null, 2),
      `df-data-export-${new Date().toISOString().slice(0, 10)}.json`,
      'application/json'
    );
  };

  const handleDownloadCSV = () => {
    if (!exportResult?.bookingsCsv) return;
    triggerDownload(
      exportResult.bookingsCsv,
      `df-bookings-export-${new Date().toISOString().slice(0, 10)}.csv`,
      'text/csv'
    );
  };

  const handleConfirmDeletion = async () => {
    setLoading(true);
    setError('');
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const initiateAccountDeletion = httpsCallable(functions, 'initiateAccountDeletion');
      const result = await initiateAccountDeletion({
        reason: deleteReason || null,
        feedback: deleteFeedback.trim() || null,
      });
      onDeleted(result.data.deletionDate);
    } catch (err) {
      console.error('Deletion initiation failed:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const overlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose();
  };

  const modalStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '16px',
  };
  const boxStyle = {
    background: 'white', borderRadius: '16px', width: '100%', maxWidth: '480px',
    padding: '32px', fontFamily: "'Montserrat', sans-serif", maxHeight: '90vh', overflowY: 'auto',
  };

  return (
    <div style={modalStyle} onClick={overlayClick}>
      <div style={boxStyle}>

        {/* ── Step 1: Download prompt ── */}
        {step === 1 && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
              Before you go...
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px', lineHeight: 1.6 }}>
              Would you like to download your data first? This includes your profile, bookings,
              {isPractitioner ? ' clients, reviews,' : ''} and session history.
            </p>

            {!exportDone ? (
              <button
                type="button"
                onClick={handleExportInModal}
                disabled={isExporting}
                style={{
                  width: '100%', padding: '11px', border: '1.5px solid #7c3aed',
                  borderRadius: '8px', background: 'white', color: '#7c3aed',
                  fontSize: '14px', fontWeight: 600, cursor: isExporting ? 'not-allowed' : 'pointer',
                  marginBottom: '12px', fontFamily: "'Montserrat', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
              >
                {isExporting ? (
                  <>
                    <span style={{ width: '14px', height: '14px', border: '2px solid #c4b5fd', borderTopColor: '#7c3aed', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                    Preparing your data...
                  </>
                ) : 'Download my data'}
              </button>
            ) : (
              <div style={{ marginBottom: '16px', padding: '14px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: '#5b21b6', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle style={{ width: '14px', height: '14px' }} /> Your data is ready!
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={handleDownloadJSON} style={{ padding: '6px 14px', border: '1px solid #7c3aed', borderRadius: '6px', background: 'white', color: '#7c3aed', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
                    Download JSON
                  </button>
                  {exportResult?.bookingsCsv && (
                    <button type="button" onClick={handleDownloadCSV} style={{ padding: '6px 14px', border: '1px solid #D1D5DB', borderRadius: '6px', background: 'white', color: '#374151', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
                      Download Bookings CSV
                    </button>
                  )}
                </div>
              </div>
            )}

            <span
              role="button"
              tabIndex={0}
              onClick={() => setStep(2)}
              onKeyDown={(e) => e.key === 'Enter' && setStep(2)}
              style={{ display: 'block', textAlign: 'center', fontSize: '13px', color: '#9CA3AF', cursor: 'pointer', userSelect: 'none', marginTop: '8px' }}
            >
              {exportDone ? 'Continue →' : 'Skip and continue'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {/* ── Step 2: Warning + reason ── */}
        {step === 2 && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
              Delete your account?
            </h2>

            <div style={{ padding: '12px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '16px' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, color: '#991B1B', marginBottom: '6px' }}>What happens when you delete:</p>
              <ul style={{ margin: 0, padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <li style={{ fontSize: '13px', color: '#7F1D1D' }}>Your account is deactivated immediately</li>
                <li style={{ fontSize: '13px', color: '#7F1D1D' }}>14-day cooling-off period — you can reactivate before then</li>
                <li style={{ fontSize: '13px', color: '#7F1D1D' }}>After 14 days, your data is permanently deleted and unrecoverable</li>
                <li style={{ fontSize: '13px', color: '#7F1D1D' }}>Financial records are retained in anonymized form for legal compliance</li>
                {isPractitioner && (
                  <>
                    <li style={{ fontSize: '13px', color: '#7F1D1D' }}>Your website is unpublished and directory listing removed</li>
                    <li style={{ fontSize: '13px', color: '#7F1D1D' }}>Your subscription is cancelled</li>
                  </>
                )}
                {!isPractitioner && (
                  <li style={{ fontSize: '13px', color: '#7F1D1D' }}>Your session history and favorites will be deleted</li>
                )}
              </ul>
            </div>

            <p style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
              Why are you leaving? <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(optional)</span>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              {reasons.map((r) => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="deleteReason"
                    value={r}
                    checked={deleteReason === r}
                    onChange={() => setDeleteReason(r)}
                    style={{ width: '15px', height: '15px', accentColor: '#7c3aed', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '13px', color: '#374151' }}>{r}</span>
                </label>
              ))}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <textarea
                value={deleteFeedback}
                onChange={(e) => setDeleteFeedback(e.target.value.slice(0, 500))}
                rows={2}
                placeholder="Anything else? (optional)"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '13px',
                  border: '1.5px solid #D1D5DB', borderRadius: '8px', outline: 'none',
                  resize: 'none', boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif",
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '11px', border: '1.5px solid #D1D5DB', borderRadius: '8px',
                  background: 'white', color: '#374151', fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Keep my account
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                style={{
                  padding: '11px', border: 'none', borderRadius: '8px',
                  background: '#FEE2E2', color: '#DC2626', fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Delete my account
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Type DELETE ── */}
        {step === 3 && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>
              This is permanent.
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px', lineHeight: 1.6 }}>
              Your account will be deactivated immediately and permanently deleted in 14 days.
              This cannot be undone.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => { setConfirmText(e.target.value); if (error) setError(''); }}
                placeholder="DELETE"
                autoComplete="off"
                style={{
                  width: '100%', padding: '10px 14px', fontSize: '14px',
                  border: `1.5px solid ${confirmText === 'DELETE' ? '#DC2626' : '#D1D5DB'}`,
                  borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              />
            </div>

            {error && (
              <div style={{ padding: '10px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: '#DC2626', margin: 0 }}>{error}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <span
                role="button"
                tabIndex={0}
                onClick={() => { if (!loading) setStep(2); }}
                onKeyDown={(e) => e.key === 'Enter' && !loading && setStep(2)}
                style={{
                  flex: 1, padding: '10px 16px', border: '1.5px solid #D1D5DB',
                  borderRadius: '8px', background: 'white', color: '#374151',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  textAlign: 'center', userSelect: 'none', fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Cancel
              </span>
              <button
                type="button"
                onClick={handleConfirmDeletion}
                disabled={confirmText !== 'DELETE' || loading}
                style={{
                  flex: 1, padding: '10px 16px', border: 'none', borderRadius: '8px',
                  background: confirmText === 'DELETE' && !loading ? '#DC2626' : '#FCA5A5',
                  color: 'white', fontSize: '14px', fontWeight: 600,
                  cursor: confirmText === 'DELETE' && !loading ? 'pointer' : 'not-allowed',
                  fontFamily: "'Montserrat', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                {loading ? (
                  <>
                    <span style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                    Deleting...
                  </>
                ) : 'Permanently delete'}
              </button>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AccountSettings — main page
// ─────────────────────────────────────────────────────────────────────────────

const AccountSettings = () => {
  const userEmail = auth?.currentUser?.email || localStorage.getItem('userEmail') || '';
  const userRole = localStorage.getItem('userRole') || 'practitioner';

  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [isPaused, setIsPaused] = useState(() => {
    return localStorage.getItem('profilePaused') === 'true';
  });
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [pausedDate, setPausedDate] = useState(() => {
    return localStorage.getItem('profilePausedDate') || null;
  });

  // Deletion state
  const [deletionScheduled, setDeletionScheduled] = useState(false);
  const [deletionDate, setDeletionDate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);

  // Load deletion state from Firestore on mount
  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => { done = true; }, 5000);
    const { onAuthStateChanged } = require('firebase/auth');
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!done && user) {
        done = true;
        clearTimeout(timeout);
        const userRef = doc(db, 'users', user.uid);
        const unsubFirestore = onSnapshot(userRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const scheduled = data?.deletion?.scheduled === true;
            setDeletionScheduled(scheduled);
            setDeletionDate(data?.deletion?.deletionDate || null);
          }
        }, (err) => {
          console.warn('⚠️ Deletion state read failed:', err);
        });
        return () => unsubFirestore();
      } else if (!done && user === null) {
        done = true;
        clearTimeout(timeout);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsubAuth(); };
  }, []);

  // Firestore fallback for pause state on mount
  useEffect(() => {
    const savedPaused = localStorage.getItem('profilePaused');
    if (savedPaused === null) {
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.getProfileStatus(userId).then(firestoreData => {
          if (firestoreData) {
            const paused = firestoreData.paused || false;
            localStorage.setItem('profilePaused', String(paused));
            if (firestoreData.pausedDate) {
              localStorage.setItem('profilePausedDate', firestoreData.pausedDate);
              setPausedDate(firestoreData.pausedDate);
            }
            setIsPaused(paused);
          }
        }).catch(err => console.warn('Firestore profile status read failed:', err));
      }
    }
  }, []);

  const handlePauseToggle = (value) => {
    if (value === true) {
      setShowPauseModal(true);
    } else {
      setIsPaused(false);
      setPausedDate(null);
      localStorage.setItem('profilePaused', 'false');
      localStorage.removeItem('profilePausedDate');
      window.dispatchEvent(new Event('profilePauseChanged'));
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.updateProfileStatus(userId, { paused: false, pausedDate: null }).catch(err => {
          console.warn('⚠️ Firestore profile status save failed:', err);
        });
      }
    }
  };

  const handleConfirmPause = () => {
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    setIsPaused(true);
    setPausedDate(dateStr);
    setShowPauseModal(false);
    localStorage.setItem('profilePaused', 'true');
    localStorage.setItem('profilePausedDate', dateStr);
    window.dispatchEvent(new Event('profilePauseChanged'));
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.updateProfileStatus(userId, {
        paused: true,
        pausedDate: dateStr,
        ...(pauseReason && { pauseReason })
      }).catch(err => {
        console.warn('⚠️ Firestore profile status save failed:', err);
      });
    }
    setPauseReason('');
  };

  const handleDeleted = (newDeletionDate) => {
    setShowDeleteModal(false);
    setDeletionScheduled(true);
    setDeletionDate(newDeletionDate);
  };

  const handleCancelDeletion = async () => {
    setReactivateLoading(true);
    try {
      const functions = getFunctions(undefined, 'us-west1');
      const cancelAccountDeletion = httpsCallable(functions, 'cancelAccountDeletion');
      await cancelAccountDeletion({});
      setDeletionScheduled(false);
      setDeletionDate(null);
    } catch (err) {
      console.error('Cancel deletion failed:', err);
      alert(err.message || 'Something went wrong. Please try again.');
    } finally {
      setReactivateLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Account & Security</span>
        </nav>

        {/* Pending Deletion Banner */}
        {deletionScheduled && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px',
            padding: '16px 20px', background: '#FEF2F2', border: '1.5px solid #FECACA',
            borderRadius: '12px', marginBottom: '24px', fontFamily: "'Montserrat', sans-serif",
          }}>
            <AlertCircle style={{ width: '20px', height: '20px', color: '#DC2626', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '15px', fontWeight: 700, color: '#991B1B', margin: '0 0 4px 0' }}>
                Your account is scheduled for deletion.
              </p>
              <p style={{ fontSize: '13px', color: '#B91C1C', margin: '0 0 12px 0' }}>
                Your data will be permanently deleted on <strong>{formatDate(deletionDate)}</strong>.
                You can cancel this before then.
              </p>
              <button
                type="button"
                onClick={handleCancelDeletion}
                disabled={reactivateLoading}
                style={{
                  padding: '8px 16px', border: 'none', borderRadius: '8px',
                  background: '#7c3aed', color: 'white', fontSize: '13px', fontWeight: 600,
                  cursor: reactivateLoading ? 'not-allowed' : 'pointer', opacity: reactivateLoading ? 0.7 : 1,
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {reactivateLoading ? 'Cancelling...' : 'Cancel deletion and reactivate'}
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Account & Security</h1>
          <p className="text-gray-600">Manage your login credentials and account settings</p>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Email Address */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Email Address</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Used for login and notifications. Never shown publicly.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="email"
                    value={userEmail}
                    disabled
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                  <button className="px-4 py-2 text-purple-600 font-medium hover:bg-purple-50 rounded-lg transition-colors">
                    Change
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Password */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Password</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Keep your account secure with a strong password.
                </p>
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors">
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Account Recovery */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Account Recovery</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Choose how you can recover your account if you forget your password.
                </p>

                {/* Email Recovery */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900">Email Recovery</span>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Enabled</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Recovery codes will be sent to: {userEmail}
                  </p>
                </div>

                {/* Phone Recovery */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">Phone Recovery (Optional)</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Add a phone number to receive recovery codes via SMS.
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-gray-700 font-medium px-3 py-2 border border-gray-300 rounded-l-lg bg-white">
                      +1
                    </span>
                    <input
                      type="tel"
                      value={recoveryPhone}
                      onChange={(e) => setRecoveryPhone(formatPhoneNumber(e.target.value))}
                      placeholder="(555) 123-4567"
                      maxLength={14}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  {recoveryPhone && (
                    <button className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors">
                      Verify Phone Number
                    </button>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-3">
                  Note: Phone number is only used for account recovery. Never shown publicly.
                </p>
              </div>
            </div>
          </div>

          {/* Pause Your Profile — practitioners only */}
          {userRole !== 'seeker' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <PauseCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Pause Your Profile</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Temporarily hide your profile while keeping your account active.
                  </p>

                  {isPaused && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-bold text-red-900 mb-1">YOUR PROFILE IS PAUSED</p>
                          <p className="text-sm text-red-800">
                            Your profile is hidden and you're not receiving new bookings. Paused on {pausedDate}.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isPaused && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-gray-700 mb-2 font-medium">When paused:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>✓ Your profile will be hidden from search immediately</li>
                        <li>✓ New bookings will be disabled</li>
                        <li>✓ You can still access your dashboard</li>
                        <li>✓ Existing bookings remain active</li>
                        <li>✓ Reactivate anytime instantly</li>
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Toggle
                        enabled={isPaused}
                        onChange={handlePauseToggle}
                      />
                      <span className={`text-sm font-medium ${isPaused ? 'text-red-600' : 'text-gray-700'}`}>
                        {isPaused ? 'Profile Paused' : 'Profile Active'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Export Your Data */}
          <ExportSection />

          {/* Delete Account */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Delete account</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Permanently delete your Divine Frameworks account and all associated data.
                  A 14-day cooling-off period applies — you can change your mind before then.
                </p>

                {deletionScheduled ? (
                  <div style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px' }}>
                    <p style={{ fontSize: '13px', color: '#991B1B', margin: 0 }}>
                      Your account is already scheduled for deletion on <strong>{formatDate(deletionDate)}</strong>.
                      Use the banner above to cancel.
                    </p>
                  </div>
                ) : (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowDeleteModal(true)}
                    onKeyDown={(e) => e.key === 'Enter' && setShowDeleteModal(true)}
                    style={{
                      fontSize: '14px', fontWeight: 600, color: '#DC2626',
                      cursor: 'pointer', userSelect: 'none', textDecoration: 'underline',
                      textDecorationColor: '#FCA5A5', fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    Delete my account
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Pause Confirmation Modal ── */}
      {showPauseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Pause Your Profile?</h3>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-amber-900 mb-2">This will:</p>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• Hide your profile from search immediately</li>
                <li>• Disable new bookings</li>
                <li>• Keep existing bookings active</li>
              </ul>
            </div>

            <p className="text-sm text-gray-700 mb-4">You can reactivate anytime.</p>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Why are you pausing? (optional)</p>
              <div className="space-y-2">
                {[
                  { value: 'vacation', label: 'Taking a vacation' },
                  { value: 'break', label: 'Need a break' },
                  { value: 'busy', label: 'Life got busy' },
                  { value: 'seasonal', label: 'Seasonal practice' },
                  { value: 'other', label: 'Other' },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="pauseReason"
                      value={value}
                      checked={pauseReason === value}
                      onChange={(e) => setPauseReason(e.target.value)}
                      className="text-purple-600"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">This helps us improve the platform.</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowPauseModal(false); setPauseReason(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPause}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors"
              >
                {pauseReason ? 'Yes, Pause Profile' : 'Skip & Pause'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <DeleteModal
          role={userRole}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default AccountSettings;
